import React, { useState, useEffect, useMemo, useRef } from "react";
import { Box, Typography, Grid2, Divider } from "@mui/material";

import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import GridViewRoundedIcon from '@mui/icons-material/GridViewRounded';
import CartLoader from "../../../Helper/Loaders/CartLoader";
import { getDiagnosticsAnalyticsList } from "../../../Api/BmsFaultsApi";
import { sortByCountDescending } from "../../../Helper/Operations/ArrayOps";
import { GroupByButton, DropdownMenu, GroupByMenuItem, GROUP_BY_OPTIONS } from "../Components/StyledComponents";
import { getTruncatedName } from "../../../Helper/Formatters/TextFormatter";
import { formatDateRange } from "../../../Helper/Analytics/AnalyticsHelper";
import RenderChartLegend from "../../AdvancedAnalytics/ContinuousChart/RenderChartLegend";
import BreakdownChart from "./BreakdownChart";
import BMSFaultChart from "../Components/StyledComponents/BMSFaultChart";
import { resolveBreakdownDimensions } from "../../../Helper/Analytics/breakdownDimensions";
import { useOrgLabels, usePagesContent } from "../../../Hooks/usePagesContent";

// Deep merge utility function
const deepMerge = (target, source) => {
  const output = { ...target };
  if (isObject(target) && isObject(source)) {
    Object.keys(source).forEach(key => {
      if (isObject(source[key])) {
        if (!(key in target)) {
          Object.assign(output, { [key]: source[key] });
        } else {
          output[key] = deepMerge(target[key], source[key]);
        }
      } else {
        Object.assign(output, { [key]: source[key] });
      }
    });
  }
  return output;
};

const isObject = (item) => {
  return item && typeof item === "object" && !Array.isArray(item);
};

// Get default chart configuration with common settings
const getDefaultChartConfig = (categories, xAxisMax, fullAlertNames, handleDataPointClick) => {
  return {
    chart: {
      id: undefined,
      type: "bar",
      height: 400,
      toolbar: {
        show: false,
      },
      zoom: {
        enabled: false,
      },
      events: {
        dataPointSelection: handleDataPointClick,
        mouseMove: function (event, chartContext, config) {
          // Get the hovered data point index
          const dataPointIndex = config.dataPointIndex;

          if (dataPointIndex >= 0) {
            // Remove previous hover backgrounds
            const allRows = document.querySelectorAll('.bar-row-hover');
            allRows.forEach(row => row.classList.remove('bar-row-hover'));

            // Add hover background to the current row
            const yAxisLabels = chartContext.el.querySelectorAll('.apexcharts-yaxis-label');
            if (yAxisLabels[dataPointIndex]) {
              const labelElement = yAxisLabels[dataPointIndex];
              // Find the parent group that represents the row
              const rowGroup = labelElement.closest('.apexcharts-yaxis-texts-g');
              if (rowGroup) {
                // Add a background rect to the entire row
                const existingBg = chartContext.el.querySelector('.hover-row-bg');
                if (existingBg) {
                  existingBg.remove();
                }

                const svgNS = "http://www.w3.org/2000/svg";
                const rect = document.createElementNS(svgNS, 'rect');
                const bbox = labelElement.getBBox();
                const chartWidth = chartContext.el.querySelector('.apexcharts-inner').getBBox().width;

                rect.setAttribute('class', 'hover-row-bg');
                rect.setAttribute('x', '0');
                rect.setAttribute('y', bbox.y - 6);
                rect.setAttribute('width', chartWidth);
                rect.setAttribute('height', bbox.height + 16);
                rect.setAttribute('fill', '#E9E9E9');
                rect.setAttribute('opacity', '0.5');

                // Insert the background before the bars
                const graphGroup = chartContext.el.querySelector('.apexcharts-inner');
                graphGroup.insertBefore(rect, graphGroup.firstChild);
              }
            }
          }
        },
        mouseLeave: function (event, chartContext, config) {
          // Remove hover background when mouse leaves
          const existingBg = chartContext.el.querySelector('.hover-row-bg');
          if (existingBg) {
            existingBg.remove();
          }
        }
      },
    },
    plotOptions: {
      bar: {
        horizontal: true,
        barHeight: "16px",
        borderRadius: 4,
        dataLabels: {
          position: 'top',
          maxItems: 100,
          hideOverflowingLabels: true,
          orientation: 'horizontal',
          total: {
            offsetX: 8,
            offsetY: 0,
            style: {
              color: '#494949',
              fontSize: '12px',
              fontFamily: 'Roboto',
              fontWeight: 400,
              lineHeight: '16px'
            }
          }
        }
      },
    },
    xaxis: {
      categories: categories,
      max: xAxisMax,
      labels: {
        show: false,
      },
      title: {
        text: "",
      },
      axisTicks: {
        show: false,
      },
      axisBorder: {
        show: false,
      },
    },
    yaxis: {
      labels: {
        style: {
          colors: "#2E2E2E",
          fontSize: "14px",
        },
        maxWidth: '30%',
        align: "left",
        offsetX: 0,
        formatter: function (value) {
          return getTruncatedName(value, 40, 40, 0);
        },
      },
      axisBorder: {
        show: false,
      },
    },
    grid: {
      show: false,
      borderColor: "#E6E6E6",
      padding: {
        left: 16,
        right: 0,
        top: -10,
        bottom: 0,
      },
      xaxis: {
        lines: {
          show: false,
        },
      },
      yaxis: {
        lines: {
          show: false,
        },
      },
    },
    stroke: {
      show: false,
    },
    tooltip: {
      shared: true,
      followCursor: false,
      intersect: false,
      x: {
        formatter: function (val, opts) {
          // Return the full alert name without truncation using the data point index
          const index = opts?.dataPointIndex;
          if (index !== undefined && fullAlertNames && fullAlertNames[index]) {
            return fullAlertNames[index];
          }
          return val;
        },
      },
    },
    states: {
      active: {
        filter: {
          type: 'none',
        },
      },
    },
  };
};

const BreakdownChartBase = ({
  title,
  height = "562px",
  startDate,
  endDate,
  hierarchicalLocation,
  alertCategories,
  globalFilters,
  config,
  chartType,
  onDataPointClick,
  minVisibilityHeight,
}) => {
  const pagesContent = usePagesContent();
  const { fleetLabel, fleetCategoryLabel } = useOrgLabels();

  // The menu, from bmsAlertV2.breakdownDimensions where the org configures it.
  // The two customer dimensions take their names from the same org meta row that
  // names them everywhere else, so renaming the filter renames these with it.
  const groupByOptions = useMemo(
    () =>
      resolveBreakdownDimensions(
        pagesContent?.metaInfo?.breakdownDimensions,
        GROUP_BY_OPTIONS,
        { fleetName: `By ${fleetLabel}`, fleetCategory: `By ${fleetCategoryLabel}` }
      ),
    [pagesContent, fleetLabel, fleetCategoryLabel]
  );

  const [groupBy, setGroupBy] = useState("originCity");
  const [selectedOption, setSelectedOption] = useState({ value: "originCity", label: "By City" });

  // The built-in default is the first dimension, but an org can drop it, and the
  // config arrives after the first render. Correct the selection once it does,
  // rather than grouping by a dimension that is no longer on the menu.
  useEffect(() => {
    if (!groupByOptions.length) return;
    if (groupByOptions.some((option) => option.value === groupBy)) return;
    setGroupBy(groupByOptions[0].value);
    setSelectedOption(groupByOptions[0]);
  }, [groupByOptions, groupBy]);

  const [groupByItems, setGroupByItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [loadingGroupBy, setLoadingGroupBy] = useState(false);
  const [loadingChart, setLoadingChart] = useState(false);
  const [dropdownAnchorEl, setDropdownAnchorEl] = useState(null);
  const selectedItemRef = useRef(null);
  const previousGroupByRef = useRef(groupBy);
  const previousSidebarDepsRef = useRef({
    groupBy,
    startDate,
    endDate,
    sidebarSelect: config.sidebarSelect,
    hierarchicalLocation,
    alertCategories,
    globalFilters,
  });

  // Fetch data when groupBy, startDate, endDate, or filters change
  useEffect(() => {
    // Check if this is the initial load (before async function to avoid closure issues)
    const isInitialLoad = groupByItems.length === 0;

    const fetchGroupByData = async () => {
      // Check if sidebar-related dependencies have changed
      const sidebarDepsChanged =
        previousSidebarDepsRef.current.groupBy !== groupBy ||
        previousSidebarDepsRef.current.startDate !== startDate ||
        previousSidebarDepsRef.current.endDate !== endDate ||
        previousSidebarDepsRef.current.sidebarSelect !== config.sidebarSelect ||
        previousSidebarDepsRef.current.hierarchicalLocation !== hierarchicalLocation ||
        previousSidebarDepsRef.current.alertCategories !== alertCategories ||
        previousSidebarDepsRef.current.globalFilters !== globalFilters;

      // Show sidebar loader if sidebar-related dependencies changed OR if it's the initial load
      // (i.e., we're actually fetching new sidebar data)
      if (sidebarDepsChanged || isInitialLoad) {
        setLoadingGroupBy(true);
        // Update the ref to track current sidebar dependencies
        previousSidebarDepsRef.current = {
          groupBy,
          startDate,
          endDate,
          sidebarSelect: config.sidebarSelect,
          hierarchicalLocation,
          alertCategories,
          globalFilters,
        };
      }
      setLoadingChart(true);

      const groupByChanged = previousGroupByRef.current !== groupBy;
      previousGroupByRef.current = groupBy;

      try {
        const response = await getDiagnosticsAnalyticsList(globalFilters, {
          startDate,
          endDate,
          select: [config.sidebarSelect],
          filters: [],
          groupBy: [groupBy],
          granularity: null
        });

        const responseData = response?.response?.data || [];

        // Transform API response to match the expected format
        const items = responseData.map(item => ({
          id: item.xLabel.toLowerCase().replace(/\s+/g, '-'),
          label: item.xLabel,
          count: item.metricsArray.find(m => m.type === config.sidebarSelect)?.value || 0
        }));

        // Sort items by count in descending order (highest count first)
        const sortedItems = sortByCountDescending(items);

        // Calculate total count for "All" option
        const totalCount = sortedItems.reduce((sum, item) => sum + item.count, 0);

        // Add "All" option at the beginning
        const allItems = [
          {
            id: "all",
            label: "All",
            count: totalCount
          },
          ...sortedItems
        ];

        // Update groupByItems if we fetched new sidebar data OR if it's the initial load (empty)
        if (sidebarDepsChanged || isInitialLoad) {
          setGroupByItems(allItems);
        }

        // Preserve selected item if it exists and groupBy didn't change, otherwise select "All" by default
        let itemToUse = allItems[0]; // Default to "All"
        if (!groupByChanged && selectedItemRef.current) {
          // Try to find the previously selected item in the new list
          const foundItem = allItems.find(item =>
            item.id === selectedItemRef.current.id ||
            item.label === selectedItemRef.current.label
          );
          if (foundItem) {
            itemToUse = foundItem;
          }
        } else if (groupByChanged) {
          // If groupBy changed, use the first item (All)
          itemToUse = allItems[0];
        }

        // Update both state and ref
        setSelectedItem(itemToUse);
        selectedItemRef.current = itemToUse;

        // Fetch chart data for the selected item
        // Prepare filters based on selected item
        const filters = [];
        if (itemToUse.id !== "all") {
          filters.push({
            name: groupBy,
            values: [itemToUse.label]
          });
        } else {
          const allItemValues = items.map(item => item.label);
          if (allItemValues.length > 0) {
            filters.push({
              name: groupBy,
              values: allItemValues
            });
          }
        }

        // Call API to get chart data
        const chartResponse = await getDiagnosticsAnalyticsList(globalFilters, {
          startDate,
          endDate,
          select: [config.chartSelect],
          filters: filters,
          groupBy: config.chartGroupBy,
          granularity: null
        });

        const chartResponseData = chartResponse?.response?.data || [];

        // Transform response to chart data format using provided transform function
        const transformedChartData = config.transformChartData(chartResponseData);

        setChartData(transformedChartData);
      } catch (error) {
        console.error("Error fetching breakdown data:", error);
        if (sidebarDepsChanged || isInitialLoad) {
          setGroupByItems([]);
          setSelectedItem(null);
        }
        setChartData([]);
      } finally {
        if (sidebarDepsChanged || isInitialLoad) {
          setLoadingGroupBy(false);
        }
        setLoadingChart(false);
      }
    };

    if (startDate && endDate) {
      fetchGroupByData();
    }
    // Only depend on config properties that affect data fetching, not chart rendering
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupBy, startDate, endDate, hierarchicalLocation, alertCategories, globalFilters, config.sidebarSelect, config.chartSelect, config.chartGroupBy, config.transformChartData]);

  // Handle item selection change
  const handleItemSelect = async (item) => {
    setSelectedItem(item);
    selectedItemRef.current = item;

    // Fetch chart data for selected item (only load the chart, not the sidebar)
    setLoadingChart(true);
    try {
      // Prepare filters based on selected item
      const filters = [];

      if (item.id !== "all") {
        // For specific item, filter by that item
        filters.push({
          name: groupBy,
          values: [item.label]
        });
      } else {
        // For "All", include all items except "All" itself
        const allItemsValues = groupByItems
          .filter(item => item.id !== "all")
          .map(item => item.label);

        if (allItemsValues.length > 0) {
          filters.push({
            name: groupBy,
            values: allItemsValues
          });
        }
      }

      // Call API to get chart data
      const response = await getDiagnosticsAnalyticsList(globalFilters, {
        startDate,
        endDate,
        select: [config.chartSelect],
        filters: filters,
        groupBy: config.chartGroupBy,
        granularity: null
      });

      const responseData = response?.response?.data || [];

      // Transform response to chart data format using provided transform function
      const transformedChartData = config.transformChartData(responseData);

      setChartData(transformedChartData);
    } catch (error) {
      console.error("Error fetching chart data:", error);
      setChartData([]);
    } finally {
      setLoadingChart(false);
    }
  };

  // Get chart config using provided function (pass additional state if available)
  const additionalState = config.getAdditionalState ? config.getAdditionalState() : {};
  const childChartConfig = config.getChartConfig(chartData, additionalState);

  // Extract dynamic values for default config
  // Get categories from child config if available, otherwise extract from chartData
  let categories = childChartConfig.options?.xaxis?.categories;
  let xAxisMax = childChartConfig.options?.xaxis?.max;
  let fullAlertNames = null;

  if (!categories && chartData && chartData.length > 0) {
    // Extract categories from chartData (assuming it has alertName property)
    categories = chartData.map(item => item.alertName || item.label || item.name);
    fullAlertNames = categories;
  } else if (categories) {
    fullAlertNames = categories;
  }

  // If xAxisMax not provided, calculate from series data
  if (!xAxisMax && childChartConfig.series && childChartConfig.series.length > 0) {
    const allValues = childChartConfig.series.flatMap(series => series.data || []);
    const maxValue = allValues.length > 0 ? Math.max(...allValues) : 0;
    xAxisMax = maxValue * 1.1; // Add 10% buffer
  }

  // Handle chart data point click - pass to parent component via config callback
  const handleDataPointClick = (event, chartContext, config) => {
    if (!chartType) return;

    const dataPointIndex = config.dataPointIndex;
    const seriesIndex = config.seriesIndex;

    // Get the fault name from categories
    const faultName = categories?.[dataPointIndex] || "";

    // Get the series name (severity or resolution type)
    const seriesNames = childChartConfig.series?.map(s => s.name) || [];
    const seriesName = seriesNames[seriesIndex] || "";

    // Get the value from the clicked series
    const value = childChartConfig.series?.[seriesIndex]?.data?.[dataPointIndex] || 0;

    // Prepare data object based on chart type
    const clickedData = {
      faultName: faultName,
      value: value,
      dataPointIndex: dataPointIndex,
      seriesIndex: seriesIndex,
      selectedItem: selectedItem,
      groupBy: selectedOption
    };

    // Set severity/resolution name based on chart type
    if (chartType === "triggeredFaultBreakdown") {
      clickedData.severityName = seriesName;
    } else if (chartType === "resolutionDurationBreakdown") {
      clickedData.resolutionType = seriesName;
    }

    // Call the onDataPointClick callback if provided
    if (onDataPointClick) {
      onDataPointClick(clickedData, groupBy);
    }
  };

  // Get default chart config
  const defaultOptions = getDefaultChartConfig(categories || [], xAxisMax || 0, fullAlertNames, handleDataPointClick);

  // Merge default options with child component's overrides
  const mergedOptions = deepMerge(defaultOptions, childChartConfig.options || {});

  // Set chart ID if custom legend is configured
  if (config.customLegend?.chartId && mergedOptions.chart) {
    mergedOptions.chart.id = config.customLegend.chartId;
  }

  // Final chart config with merged options
  const chartConfig = {
    series: childChartConfig.series || [],
    options: mergedOptions,
  };

  const chartPadding = config.chartPadding || "12px 28px 24px";

  return (
    <Box
      sx={{
        backgroundColor: "#FFFFFF",
        borderRadius: "8px",
        border: "1px solid #E6E6E6",
        overflow: "hidden",
        height: height
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "24px 22px",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Typography
            variant="fw600s16lh24c444B51"
            sx={{ color: "#2E2E2E" }}
          >
            {title}
          </Typography>
        </Box>
        <Typography variant="fw400s12c686868" sx={{ lineHeight: "20px" }}>
          {formatDateRange(startDate, endDate)}
        </Typography>
      </Box>

      {/* Content - Two sections */}
      <Box sx={{ display: "flex", height: `calc(${height} - 72px)`, overflow: "hidden" }}>
        {/* Left Sidebar */}
        <Box
          sx={{
            width: "320px",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          {/* Group By Dropdown */}
          <Box
            sx={{
              padding: "8px 16px",
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <GroupByButton
                onClick={(e) => setDropdownAnchorEl(e.currentTarget)}
                endIcon={<ArrowDropDownIcon sx={{ color: "#5E758D", fontSize: "10px" }} />}
                startIcon={<GridViewRoundedIcon sx={{ color: "#5E758D", fontSize: "12px" }} />}
              >
                {groupByOptions.find(opt => opt.value === groupBy)?.label}
              </GroupByButton>
              <Typography variant="fw400s12c686868">ISSUES</Typography>
            </Box>

            <Divider sx={{ ml: 0.5, borderColor: '#E4E4E4' }} />

            <DropdownMenu
              anchorEl={dropdownAnchorEl}
              open={Boolean(dropdownAnchorEl)}
              onClose={() => setDropdownAnchorEl(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            >
              {groupByOptions.map((option) => (
                <GroupByMenuItem
                  key={option.value}
                  selected={groupBy === option.value}
                  onClick={() => {
                    setGroupBy(option.value);
                    setSelectedOption(option);
                    setDropdownAnchorEl(null);
                  }}
                >
                  {option.label}
                </GroupByMenuItem>
              ))}
            </DropdownMenu>
          </Box>

          {/* Items List */}
          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              padding: "8px 8px 24px",
            }}
          >
            {loadingGroupBy ? (
              <Box sx={{ padding: "16px", display: "flex", justifyContent: "center" }}>
                <CartLoader />
              </Box>
            ) : groupByItems.length === 0 ? (
              <Box sx={{ padding: "16px", textAlign: "center" }}>
                <Typography variant="fw400s14c686868">No data available</Typography>
              </Box>
            ) : (
              <Grid2 container gap={0.5} flexDirection={'column'}>
                {groupByItems.map((item) => (
                  <Box
                    key={item.id}
                    onClick={() => handleItemSelect(item)}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "12px 16px",
                      borderRadius: "8px",
                      cursor: "pointer",
                      backgroundColor: selectedItem?.id === item.id ? "#F7F7FF" : "transparent",
                      "&:hover": {
                        backgroundColor: selectedItem?.id === item.id ? "#F7F7FF" : "#F8F8F8",
                      },
                      transition: "background-color 0.2s",
                    }}
                  >
                    <Typography
                      variant="fw400s14c000000"
                      sx={{
                        color: selectedItem?.id === item.id ? "#4D44D8" : "#202124",
                        fontWeight: selectedItem?.id === item.id ? 600 : 400,
                        lineHeight: '14px',
                        textTransform: groupBy === 'cellChemistry' && item.id !== 'all' ? 'uppercase' : 'capitalize'
                      }}
                    >
                      {item.label || 'Not Set'}
                    </Typography>
                    <Typography
                      variant="fw600s14c000000"
                      sx={{
                        color: selectedItem?.id === item.id ? "#4D44D8" : "#202124",
                        fontWeight: selectedItem?.id === item.id ? 600 : 500,
                        lineHeight: '14px',
                      }}
                    >
                      {item.count}
                    </Typography>
                  </Box>
                ))}
              </Grid2>
            )}
          </Box>
        </Box>

        <Divider orientation="vertical" flexItem sx={{ borderColor: '#E4E4E4', mb: 6 }} />

        {/* Right Section - Chart/Graph Area */}
        <Box
          sx={{
            flex: 1,
            padding: chartPadding,
            display: "flex",
            flexDirection: "column",
            gap: 2,
            overflow: "hidden",
          }}
        >
          <Box>
            {/* Chart-specific controls (e.g., toggle buttons) */}
            {config.renderChartControls && config.renderChartControls()}

            {/* Custom Legend */}
            {config.customLegend && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                {config.customLegend.label && (
                  <Typography variant="fw600s12c8D8D8D" sx={{ textTransform: 'uppercase', color: '#8F8F8F' }}>
                    {config.customLegend.label}
                  </Typography>
                )}
                <Box sx={{ flex: 1, display: 'flex', justifyContent: config.customLegend.centered ? 'center' : 'flex-end' }}>
                  <RenderChartLegend
                    legendSeries={config.customLegend.series}
                    chartIDs={[config.customLegend.chartId]}
                    isLoading={loadingChart}
                    centered={config.customLegend.centered}
                  />
                </Box>
              </Box>
            )}
          </Box>

          {/* Chart */}
          <Box sx={{ flex: 1 }}>
            {loadingChart ? (
              <Box sx={{ padding: "32px", display: "flex", justifyContent: "center", alignItems: "center", minHeight: "300px" }}>
                <CartLoader />
              </Box>
            ) : chartData.length === 0 || chartConfig.series.length === 0 || chartConfig.series[0]?.data?.length === 0 ? (
              <Box sx={{ padding: "32px", textAlign: "center" }}>
                <Typography variant="fw400s14c686868">
                  {typeof config.emptyStateMessage === 'function'
                    ? config.emptyStateMessage(chartData)
                    : (config.emptyStateMessage || "No chart data available")}
                </Typography>
              </Box>
            ) : (
              <BMSFaultChart drillDownEnabled={true}>
                <BreakdownChart
                  series={chartConfig.series}
                  categories={chartConfig.options?.xaxis?.categories || []}
                  options={{
                    ...chartConfig.options,
                    chart: {
                      ...chartConfig.options?.chart,
                      events: {
                        ...chartConfig.options?.chart?.events,
                        dataPointSelection: handleDataPointClick
                      }
                    }
                  }}
                  xAxisTickFormatter={config.xAxisTickFormatter}
                  minVisibilityHeight={minVisibilityHeight}
                />
              </BMSFaultChart>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default BreakdownChartBase;
